import { useState, useEffect, useRef, type FormEvent, type KeyboardEvent, type ClipboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { api, ApiError } from '../lib/api';
import { Navbar } from '../components/Navbar';

type Step = 'email' | 'code' | 'password' | 'done';

export function ForgotPassword() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const codeInputs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  // Auto-focus first code input on entering code step
  useEffect(() => {
    if (step === 'code') codeInputs.current[0]?.focus();
  }, [step]);

  /* ── Step 1: request code ── */
  async function onSubmitEmail(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await api('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setStep('code');
      setResendCooldown(30);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') setError('Demasiados intentos. Espera unos minutos.');
      else setError('Algo ha fallado. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  }

  /* ── Resend code ── */
  async function onResend() {
    if (resendCooldown > 0) return;
    setError(null);
    setInfo(null);
    try {
      await api('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setInfo('Te hemos enviado un nuevo código.');
      setCode(['', '', '', '', '', '']);
      codeInputs.current[0]?.focus();
      setResendCooldown(30);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') setError('Demasiados intentos. Espera unos minutos.');
      else setError('No pudimos reenviar el código. Inténtalo de nuevo.');
    }
  }

  /* ── Step 2: verify code ── */
  async function onSubmitCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const joined = code.join('');
    if (joined.length !== 6) {
      setError('Introduce el código completo de 6 dígitos.');
      return;
    }
    setSubmitting(true);
    try {
      await api('/api/auth/verify-reset-code', {
        method: 'POST',
        body: JSON.stringify({ email, code: joined }),
      });
      setStep('password');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'INVALID_RESET_CODE') setError('El código no es correcto. Revísalo e inténtalo de nuevo.');
        else if (err.code === 'RESET_CODE_EXPIRED') setError('El código ha caducado. Solicita uno nuevo.');
        else if (err.code === 'RESET_CODE_TOO_MANY_ATTEMPTS') setError('Demasiados intentos. Solicita un código nuevo.');
        else if (err.code === 'RATE_LIMITED') setError('Demasiados intentos. Espera unos minutos.');
        else setError('Algo ha fallado. Inténtalo de nuevo.');
      } else setError('Algo ha fallado. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  }

  /* ── Step 3: reset password ── */
  async function onSubmitPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSubmitting(true);
    try {
      await api('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ email, code: code.join(''), newPassword: password }),
      });
      setStep('done');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'INVALID_RESET_CODE') setError('El código ya no es válido. Vuelve a empezar.');
        else if (err.code === 'RESET_CODE_EXPIRED') setError('El código ha caducado. Solicita uno nuevo.');
        else if (err.code === 'RATE_LIMITED') setError('Demasiados intentos. Espera unos minutos.');
        else setError('Algo ha fallado. Inténtalo de nuevo.');
      } else setError('Algo ha fallado. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  }

  /* ── Code input helpers ── */
  function setDigit(i: number, val: string) {
    const next = [...code];
    next[i] = val;
    setCode(next);
  }
  function onCodeChange(i: number, raw: string) {
    const digit = raw.replace(/\D/g, '').slice(-1);
    setDigit(i, digit);
    if (digit && i < 5) codeInputs.current[i + 1]?.focus();
  }
  function onCodeKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !code[i] && i > 0) {
      codeInputs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) codeInputs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < 5) codeInputs.current[i + 1]?.focus();
  }
  function onCodePaste(e: ClipboardEvent<HTMLInputElement>) {
    const txt = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < txt.length; i++) next[i] = txt[i]!;
    setCode(next);
    const focusIdx = Math.min(txt.length, 5);
    codeInputs.current[focusIdx]?.focus();
  }

  /* ── Render ── */
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundImage: 'url(/bg-login.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl shadow-blue-100/50 border border-slate-100 px-10 py-10">
          <div className="flex flex-col items-center gap-2 mb-8">
            <img src="/zettapost-logo-min.png" alt="Zettapost" className="h-10 w-auto mb-1" />
            {step === 'email' && <>
              <h1 className="text-2xl font-bold text-slate-900">¿Olvidaste tu contraseña?</h1>
              <p className="text-sm text-slate-400 text-center">Introduce tu email y te enviaremos un código para restablecerla.</p>
            </>}
            {step === 'code' && <>
              <h1 className="text-2xl font-bold text-slate-900">Revisa tu correo</h1>
              <p className="text-sm text-slate-400 text-center">
                Hemos enviado un código de 6 dígitos a<br />
                <span className="font-medium text-slate-600">{email}</span>
              </p>
            </>}
            {step === 'password' && <>
              <h1 className="text-2xl font-bold text-slate-900">Nueva contraseña</h1>
              <p className="text-sm text-slate-400 text-center">Elige una contraseña segura para tu cuenta.</p>
            </>}
            {step === 'done' && <>
              <div className="rounded-full bg-emerald-100 p-3 mb-2">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">¡Listo!</h1>
              <p className="text-sm text-slate-400 text-center">Tu contraseña ha sido actualizada correctamente.</p>
            </>}
          </div>

          {/* Step 1: Email */}
          {step === 'email' && (
            <form className="space-y-4" onSubmit={onSubmitEmail}>
              <div>
                <label className="label" htmlFor="email">Email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    required
                    placeholder="ejemplo@zettapost.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input pl-10"
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</p>
              )}

              <button type="submit" disabled={submitting} className="btn-primary w-full !py-3 !text-base mt-1">
                {submitting ? 'Enviando…' : 'Enviar código'}
              </button>

              <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mt-3">
                <ArrowLeft size={14} /> Volver a iniciar sesión
              </Link>
            </form>
          )}

          {/* Step 2: Code */}
          {step === 'code' && (
            <form className="space-y-4" onSubmit={onSubmitCode}>
              <div className="flex justify-center gap-2" onPaste={onCodePaste}>
                {code.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => { codeInputs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={d}
                    onChange={(e) => onCodeChange(i, e.target.value)}
                    onKeyDown={(e) => onCodeKeyDown(i, e)}
                    className="w-12 h-14 text-center text-2xl font-bold text-slate-900 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition"
                  />
                ))}
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</p>
              )}
              {info && !error && (
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">{info}</p>
              )}

              <button type="submit" disabled={submitting} className="btn-primary w-full !py-3 !text-base">
                {submitting ? 'Verificando…' : 'Verificar código'}
              </button>

              <div className="text-center text-sm text-slate-500">
                ¿No has recibido el código?{' '}
                <button
                  type="button"
                  onClick={onResend}
                  disabled={resendCooldown > 0}
                  className="text-brand-600 font-medium hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
                >
                  {resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 'Reenviar'}
                </button>
              </div>

              <button
                type="button"
                onClick={() => { setStep('email'); setCode(['','','','','','']); setError(null); setInfo(null); }}
                className="flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mt-3 w-full"
              >
                <ArrowLeft size={14} /> Cambiar email
              </button>
            </form>
          )}

          {/* Step 3: New password */}
          {step === 'password' && (
            <form className="space-y-4" onSubmit={onSubmitPassword}>
              {/* Lets the password manager know which account the new password belongs to. */}
              <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />
              <div>
                <label className="label" htmlFor="newPassword">Nueva contraseña</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    placeholder="Al menos 8 caracteres"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="label" htmlFor="confirmPassword">Confirmar contraseña</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    placeholder="Repite la contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input pl-10"
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</p>
              )}

              <button type="submit" disabled={submitting} className="btn-primary w-full !py-3 !text-base mt-1">
                {submitting ? 'Guardando…' : 'Cambiar contraseña'}
              </button>
            </form>
          )}

          {/* Step 4: Done */}
          {step === 'done' && (
            <Link to="/login" className="btn-primary w-full !py-3 !text-base block text-center">
              Iniciar sesión
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
